import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Star, Eye, Heart, Trophy, Clock } from 'lucide-react';
import api from '@/lib/api';
import { Card, Button, Skeleton } from '@/components/ui';

interface ProgressData {
  animals_viewed: number;
  stars: number;
  achievements_earned: number;
  recent: {
    id: number;
    name: string;
    slug: string;
    image_url: string;
    last_viewed_at: string;
  }[];
  favorites: {
    id: number;
    name: string;
    slug: string;
    image_url: string;
  }[];
}

export function Profile() {
  const { data: progress, isLoading, error } = useQuery({
    queryKey: ['progress'],
    queryFn: async () => {
      const { data } = await api.get<ProgressData>('/api/progress');
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="relative z-10 space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative z-10 text-center py-12">
        <span className="text-6xl mb-4 block">😿</span>
        <h1 className="text-2xl font-bold mb-2">Не удалось загрузить профиль</h1>
        <p className="text-gray-400 mb-6">Попробуйте позже</p>
        <Link to="/">
          <Button>На главную</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="relative z-10 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="w-24 h-24 mx-auto mb-4 bg-cosmic-purple/20 rounded-full flex items-center justify-center">
          <span className="text-5xl">👤</span>
        </div>
        <h1 className="text-3xl font-bold mb-2">Мой профиль</h1>
        <p className="text-gray-400">Твой прогресс в Зоопарке Галактики</p>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        <Card className="text-center">
          <Star className="w-8 h-8 mx-auto mb-2 text-star-gold" />
          <p className="text-3xl font-bold text-star-gold">{progress?.stars || 0}</p>
          <p className="text-sm text-gray-400">Звёзд</p>
        </Card>

        <Card className="text-center">
          <Eye className="w-8 h-8 mx-auto mb-2 text-cosmic-purple" />
          <p className="text-3xl font-bold">{progress?.animals_viewed || 0}</p>
          <p className="text-sm text-gray-400">Животных изучено</p>
        </Card>

        <Card className="text-center">
          <Trophy className="w-8 h-8 mx-auto mb-2 text-cosmic-cyan" />
          <p className="text-3xl font-bold">{progress?.achievements_earned || 0}</p>
          <p className="text-sm text-gray-400">Достижений</p>
        </Card>
      </motion.div>

      {/* Favorites */}
      {progress?.favorites && progress.favorites.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Избранное
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {progress.favorites.map((animal) => (
              <Link key={animal.id} to={`/animal/${animal.slug}`}>
                <Card image={animal.image_url} imageAlt={animal.name} hover>
                  <h3 className="font-semibold truncate">{animal.name}</h3>
                </Card>
              </Link>
            ))}
          </div>
        </motion.section>
      )}

      {/* Recent */}
      {progress?.recent && progress.recent.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Недавно просмотренные
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {progress.recent.map((animal) => (
              <Link key={animal.id} to={`/animal/${animal.slug}`}>
                <Card image={animal.image_url} imageAlt={animal.name} hover>
                  <h3 className="font-semibold truncate text-sm">{animal.name}</h3>
                </Card>
              </Link>
            ))}
          </div>
        </motion.section>
      )}

      {/* Empty state */}
      {(!progress?.recent || progress.recent.length === 0) &&
        (!progress?.favorites || progress.favorites.length === 0) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center py-12"
          >
            <span className="text-6xl mb-4 block">🌟</span>
            <h2 className="text-xl font-bold mb-2">Начни исследовать!</h2>
            <p className="text-gray-400 mb-6">
              Изучай животных, чтобы заполнить свой профиль
            </p>
            <Link to="/">
              <Button>Перейти к животным</Button>
            </Link>
          </motion.div>
        )}

      {/* Link to achievements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-center"
      >
        <Link to="/achievements">
          <Button variant="secondary" icon={<Trophy className="w-4 h-4" />}>
            Все достижения
          </Button>
        </Link>
      </motion.div>
    </div>
  );
}
